import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { versions } from "@/lib/kbApi";
import type { ArticleVersion } from "@/types/api";

// Hook to fetch version history for an article
export function useVersions(articleSlug: string) {
  return useQuery({
    queryKey: ["versions", articleSlug],
    queryFn: () => versions.list(articleSlug),
    enabled: !!articleSlug,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Hook to fetch a specific version
export function useVersion(articleSlug: string, versionId: number) {
  return useQuery({
    queryKey: ["version", articleSlug, versionId],
    queryFn: () => versions.get(articleSlug, versionId),
    enabled: !!articleSlug && !!versionId,
  });
}

// Hook to revert to a version
export function useRevertVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      articleSlug,
      versionId,
      changeSummary
    }: {
      articleSlug: string;
      versionId: number;
      changeSummary?: string;
    }) => {
      return versions.revert(articleSlug, versionId, changeSummary);
    },
    onSuccess: (data, { articleSlug }) => {
      // Invalidate and refetch related queries
      queryClient.invalidateQueries({ queryKey: ["article", articleSlug] });
      queryClient.invalidateQueries({ queryKey: ["articles"] });
      queryClient.invalidateQueries({ queryKey: ["versions", articleSlug] });
    },
  });
}

// Hook to compare versions (future enhancement)
export function useCompareVersions() {
  return useMutation({
    mutationFn: ({
      articleSlug,
      fromVersion,
      toVersion
    }: {
      articleSlug: string;
      fromVersion: number;
      toVersion: number;
    }) => {
      return versions.compare(articleSlug, fromVersion, toVersion);
    },
  });
}